/**
 * Paya ID hosts must not offer OS Buddy password reset (#1970).
 *
 * Login already hides the password form and the forgot-password link when
 * `oidcEnabled` is true. `/forgot-password` and `/reset-password` stay in the
 * router for bookmarks and mailed links, so the pages themselves send the
 * browser back to `/login`.
 *
 * Loading-safe: do not redirect until `/auth/context` has answered — a
 * password-only environment must never be bounced away because the query is
 * still in flight. While waiting, or while Paya ID is known on, the caller
 * must not render the reset form. Showing it first would flash a working form
 * on a Paya ID host (and a fast submit would reset a password that cannot
 * sign anyone in). A failed context fetch must not lock a password-only host
 * out, so an error is treated as "not Paya ID".
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/hooks/useAuth';

export function useOidcPasswordGuard(): { blocked: boolean } {
  const navigate = useNavigate();
  const { data: authContext, isError } = useAuthContext();
  const oidcEnabled = authContext?.oidcEnabled === true;
  const stillLoading = authContext === undefined && isError !== true;

  useEffect(() => {
    if (!oidcEnabled) return;
    void navigate('/login', { replace: true });
  }, [oidcEnabled, navigate]);

  return { blocked: stillLoading || oidcEnabled };
}
